import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors, UserActions, UserSelectors } from '@core/account-data';
import type { User } from '@core/account-types';
import { filterBoolean } from '@core/common-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { EmailProvidersActions } from '@core/email-providers-data';
import type { SharedEmailProvider } from '@core/email-providers-types';
import { I18nService } from '@core/i18n-legacy';
import type { Select2GroupWithItems } from '@core/user-interface-legacy';
import { combineLatestBatch } from '@core/utils-legacy';
import { UnifiedJobFull } from '@recruitee/job-editors-shared-types';
import type {
  MailboxAASenderSelectItem,
  MailboxSelectAdmin,
  MailboxSelectItem,
} from '@recruitee/mailbox-ui';
import {
  MailboxSelectHiringManager,
  MailboxSelectRecruiter,
  MailboxSelectType,
  MailboxSelectUserPerformingAction,
} from '@recruitee/mailbox-ui';

import { SharedEmailProvidersFacade } from '../../facade/shared-email-providers.facade';

@Injectable()
export class AutomatedActionsEmailSenderSelect {
  private readonly store = inject(Store);
  private readonly sharedMailboxFacade = inject(SharedEmailProvidersFacade);
  private readonly i18n = inject(I18nService);

  private readonly hasAaImprovements = injectDevFlag(DevFlag.AaImprovements);

  public currentUser$ = this.store.select(CurrentUserSelectors.data).pipe(filterBoolean);
  public users$ = this.store.select(UserSelectors.idMap).pipe(filterBoolean);
  public refreshItems$ = new BehaviorSubject<void>(undefined);
  public mailboxItems$ = combineLatestBatch([
    this.store.select(UserSelectors.entities()).pipe(filterBoolean),
    this.sharedMailboxFacade.providers$,
    this.refreshItems$,
  ]).pipe(
    map(
      ([users, sharedProviders]): Array<Select2GroupWithItems<MailboxSelectItem>> =>
        <Array<Select2GroupWithItems<MailboxAASenderSelectItem>>>[
          this.getTokenizedSendersGroup(users),
          this.getPersonalSendersGroup(users),
          ...(this.hasAaImprovements ? [this.getMembersWithoutAccessGroup(users)] : []),
          this.getSharedMailboxSendersGroup(sharedProviders),
        ],
    ),
  );
  public job?: Pick<UnifiedJobFull, 'id' | 'hiringManagerId' | 'recruiterId'>;

  constructor() {
    this.store.dispatch(UserActions.fetchCollection());
    this.store.dispatch(EmailProvidersActions.fetch());
  }

  public itemFilterFn = (item: MailboxAASenderSelectItem) => true;
  public setFilterFn(fn: (item: MailboxAASenderSelectItem) => boolean): void {
    this.itemFilterFn = fn;
  }

  public setJob(job: Pick<UnifiedJobFull, 'id' | 'hiringManagerId' | 'recruiterId'>): void {
    this.job = job;
  }

  public refreshItems(): void {
    this.refreshItems$.next();
  }

  private getTokenizedSendersGroup(users: User[]) {
    const hasNotAssignedRecruiter = this.job
      ? !users.find(user => user.id === this.job!.recruiterId)
      : false;
    const hasNotAssignedHiringManager = this.job
      ? !users.find(user => user.id === this.job!.hiringManagerId)
      : false;
    return {
      name: '',
      items: [
        {
          label: this.i18n.translate('app.automated_actions.sender.user_performing_action'),
          value: MailboxSelectUserPerformingAction,
        },
        {
          label: this.i18n.translate('app.automated_actions.sender.recruiter'),
          value: MailboxSelectRecruiter,
          badge: hasNotAssignedRecruiter
            ? {
                icon: 'warning',
                color: 'warning-tint',
                label: this.i18n.translate('app.common.not_assigned'),
                tooltip: this.i18n.translate(
                  'app.automated_actions.sender.recruiter_not_assigned_tooltip',
                ),
              }
            : undefined,
        },
        {
          label: this.i18n.translate('app.automated_actions.sender.hiring_manager'),
          value: MailboxSelectHiringManager,
          badge: hasNotAssignedHiringManager
            ? {
                icon: 'warning',
                color: 'warning-tint',
                label: this.i18n.translate('app.common.not_assigned'),
                tooltip: this.i18n.translate(
                  'app.automated_actions.sender.hiring_manager_not_assigned_tooltip',
                ),
              }
            : undefined,
        },
      ].filter(item => this.itemFilterFn(<MailboxAASenderSelectItem>item)),
    };
  }

  private getSharedMailboxSendersGroup(
    sharedProviders: SharedEmailProvider[],
  ): Select2GroupWithItems<MailboxSelectItem> {
    return {
      name: this.i18n.translate('app.shared_mailboxes.aa_sender_select.group_shared_mailboxes'),
      items: <MailboxAASenderSelectItem[]>sharedProviders
        .filter(({ connected }) => connected)
        .map(provider => ({
          label: provider.name ?? provider.email,
          value: {
            type: MailboxSelectType.Shared,
            id: provider.id,
            provider,
          },
        }))
        .filter(item => this.itemFilterFn(<MailboxAASenderSelectItem>item)),
    };
  }

  private getPersonalSendersGroup(users: User[]): Select2GroupWithItems<MailboxSelectItem> {
    return {
      name: this.i18n.translate('app.shared_mailboxes.aa_sender_select.group_personal'),
      items: users
        .filter(
          user =>
            user.roleAbilities.includes(Ability.SendEmails) &&
            (this.job?.id ? user.viewAllJobs || user.offerIds?.includes(this.job?.id) : true),
        )
        .map(user => ({
          label: `${user!.firstName} ${user!.lastName}`,
          value: <MailboxSelectAdmin>{
            type: MailboxSelectType.AutomatedActionAdmin,
            id: user.id,
            user,
          },
        }))
        .filter(item => this.itemFilterFn(<MailboxAASenderSelectItem>item)),
    };
  }

  private getMembersWithoutAccessGroup(users: User[]): Select2GroupWithItems<MailboxSelectItem> {
    return {
      name: this.i18n.translate('app.shared_mailboxes.aa_sender_select.members_without_access'),
      items: users
        .filter(
          user =>
            !(
              user.roleAbilities.includes(Ability.SendEmails) &&
              (this.job?.id ? user.viewAllJobs || user.offerIds?.includes(this.job?.id) : true)
            ),
        )
        .map(user => ({
          label: `${user!.firstName} ${user!.lastName}`,
          disabled: true,
          disabledTooltip: this.i18n.translate(
            'app.shared_mailboxes.aa_sender_select.members_without_access_tooltip',
          ),
          value: <MailboxSelectAdmin>{
            type: MailboxSelectType.AutomatedActionAdmin,
            id: user.id,
            user,
          },
        }))
        .filter(item => this.itemFilterFn(<MailboxAASenderSelectItem>item)),
    };
  }
}
